import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, Device, DevicePermission } from '../utils/api';
import {
  MagnifyingGlassIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const DeviceManagement: React.FC = () => {
  const [searchUserId, setSearchUserId] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const queryClient = useQueryClient();

  const { data: devices, isLoading, error } = useQuery<Device[]>({
    queryKey: ['devices', searchUserId, statusFilter],
    queryFn: async () => {
      const response = await adminApi.getDevices({
        user_id: searchUserId || undefined,
        status: statusFilter || undefined,
        limit: 100,
      });
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: devicePermissions } = useQuery<DevicePermission[]>({
    queryKey: ['device-permissions', selectedDevice?.id],
    queryFn: async () => {
      if (!selectedDevice) return [];
      const response = await adminApi.getDevicePermissions({
        device_id: selectedDevice.id,
      });
      return response.data;
    },
    enabled: !!selectedDevice,
  });

  const revokeDeviceMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      await adminApi.revokeDevice(deviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setSelectedDevice(null);
    },
  });

  const handleRevokeDevice = async (device: Device) => {
    if (window.confirm(`Are you sure you want to revoke device "${device.name}"? This action cannot be undone.`)) {
      revokeDeviceMutation.mutate(device.id);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      paired: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      inactive: 'bg-gray-100 text-gray-800',
      revoked: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || statusColors.inactive}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <span className="text-red-700">Failed to load devices</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Device Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage all registered devices in the mesh network
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <span className="text-sm text-gray-700">
            Showing {devices?.length || 0} devices
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label htmlFor="search-user" className="block text-sm font-medium text-gray-700 mb-2">
              Filter by User ID
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search-user"
                className="admin-input pl-10"
                placeholder="Enter user ID to filter devices..."
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-2">
              Status Filter
            </label>
            <select
              id="status-filter"
              className="admin-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="paired">Paired</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mesh IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  VPN Hosting
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Heartbeat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {devices?.map((device) => (
                <tr key={device.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center">
                          <DevicePhoneMobileIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {device.name}
                        </div>
                        <div className="text-sm text-gray-500">{device.description || 'No description'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {device.platform}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(device.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {device.mesh_ip || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {device.is_hosting_enabled ? (
                        <>
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Enabled
                          </span>
                          {device.active_sessions_count ? (
                            <span className="text-xs text-gray-600">
                              ({device.active_sessions_count} active)
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                          Disabled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(device.last_heartbeat_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedDevice(device)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Details
                    </button>
                    {device.status !== 'revoked' && (
                      <button
                        onClick={() => handleRevokeDevice(device)}
                        disabled={revokeDeviceMutation.isPending}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {devices && devices.length === 0 && (
          <div className="text-center py-12">
            <DevicePhoneMobileIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No devices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters.
            </p>
          </div>
        )}
      </div>

      {/* Device Details Modal */}
      {selectedDevice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Device Details</h3>
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Device Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedDevice.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Platform</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedDevice.platform}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="mt-1">{getStatusBadge(selectedDevice.status)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mesh IP</label>
                    <p className="mt-1 text-sm font-mono text-gray-900">
                      {selectedDevice.mesh_ip || 'Not assigned'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedDevice.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paired</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedDevice.paired_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Heartbeat</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedDevice.last_heartbeat_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Seen</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedDevice.last_seen_at)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">Can Route Traffic</p>
                    {selectedDevice.can_route_traffic ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mx-auto mt-1" />
                    ) : (
                      <XCircleIcon className="h-6 w-6 text-gray-400 mx-auto mt-1" />
                    )}
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">Can Accept Routes</p>
                    {selectedDevice.can_accept_routes ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mx-auto mt-1" />
                    ) : (
                      <XCircleIcon className="h-6 w-6 text-gray-400 mx-auto mt-1" />
                    )}
                  </div>
                </div>

                {/* VPN Hosting Information */}
                <div className="pt-4 mt-4 border-t">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    VPN Hosting
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Status</span>
                      {selectedDevice.is_hosting_enabled ? (
                        <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-800 font-medium">
                          Enabled
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-sm rounded-full bg-gray-200 text-gray-700">
                          Disabled
                        </span>
                      )}
                    </div>

                    {selectedDevice.is_hosting_enabled && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Active Sessions</span>
                          <span className="text-sm text-gray-900 font-mono">
                            {selectedDevice.active_sessions_count || 0}
                          </span>
                        </div>

                        {selectedDevice.wireguard_ip && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">VPN IP</span>
                            <span className="text-sm text-gray-900 font-mono">
                              {selectedDevice.wireguard_ip}
                            </span>
                          </div>
                        )}

                        {selectedDevice.wireguard_public_key && (
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-700 mb-1">WireGuard Public Key</span>
                            <span className="text-xs text-gray-600 font-mono break-all bg-white p-2 rounded border">
                              {selectedDevice.wireguard_public_key}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Device Permissions */}
                <div className="pt-4 mt-4 border-t">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Permissions ({devicePermissions?.length || 0})
                  </h4>
                  {devicePermissions && devicePermissions.length > 0 ? (
                    <div className="space-y-2">
                      {devicePermissions.map((permission) => (
                        <div key={permission.id} className="bg-gray-50 rounded p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {permission.grantee_name || `User ${permission.grantee_user_id}`}
                              </p>
                              <p className="text-xs text-gray-500">
                                {permission.can_route_traffic && 'Can route'}
                                {permission.can_route_traffic && permission.can_accept_routes && ' • '}
                                {permission.can_accept_routes && 'Can accept routes'}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              permission.status === 'approved' ? 'bg-green-100 text-green-800' :
                              permission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {permission.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No permissions granted</p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedDevice(null)}
                  className="admin-button-secondary"
                >
                  Close
                </button>
                {selectedDevice.status !== 'revoked' && (
                  <button
                    onClick={() => handleRevokeDevice(selectedDevice)}
                    disabled={revokeDeviceMutation.isPending}
                    className="admin-button-danger"
                  >
                    Revoke Device
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;
